import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { getData, postData, putData } from '../../../../webService/webservice.js';
import TVFMLoader from '../../Loader/loader.jsx';
import '../../../../styles/Auth/settings.css';

const LegalDocumentManagement = () => {
    const { docType } = useParams();
    const navigate = useNavigate();

    // Determine initial document type from URL route or default to PRIVACY_POLICY
    const getActiveTypeFromUrl = () => {
        if (docType === 'terms-and-conditions' || docType === 'TERMS_AND_CONDITIONS') {
            return 'TERMS_AND_CONDITIONS';
        }
        return 'PRIVACY_POLICY';
    };

    const [activeType, setActiveType] = useState(getActiveTypeFromUrl());
    const [loading, setLoading] = useState(false);

    // Current Working Document Data
    const [docData, setDocData] = useState(null);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [version, setVersion] = useState('');
    const [effectiveDate, setEffectiveDate] = useState('');
    const [status, setStatus] = useState('DRAFT');
    const [isEditing, setIsEditing] = useState(false);

    // Modals
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyList, setHistoryList] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Keep activeType in sync with route URL
    useEffect(() => {
        const typeFromUrl = getActiveTypeFromUrl();
        if (typeFromUrl !== activeType) {
            setActiveType(typeFromUrl);
        }
    }, [docType]);

    // Fetch Working Document whenever activeType changes
    const fetchDocument = async (typeToFetch) => {
        setLoading(true);
        try {
            const token = sessionStorage.getItem('Token') || localStorage.getItem('Token');
            const res = await getData(`legal-documents/${typeToFetch}`, {}, token);

            if (res && (res.status === 200 || res.success !== false) && res.data && typeof res.data === 'object') {
                const doc = res.data;
                setDocData(doc);
                setTitle(doc.title || (typeToFetch === 'PRIVACY_POLICY' ? 'Privacy Policy' : 'Terms & Conditions'));
                setContent(doc.content || '');
                setVersion(doc.version || '1.0');
                setStatus(doc.status || 'DRAFT');
                if (doc.effectiveDate) {
                    setEffectiveDate(new Date(doc.effectiveDate).toISOString().split('T')[0]);
                } else {
                    setEffectiveDate(new Date().toISOString().split('T')[0]);
                }
                setIsEditing(doc.status === 'DRAFT');
            } else {
                // If no document exists, set defaults for new draft
                setDocData(null);
                setTitle(typeToFetch === 'PRIVACY_POLICY' ? 'Privacy Policy' : 'Terms & Conditions');
                setContent('');
                setVersion('1.0');
                setStatus('DRAFT');
                setEffectiveDate(new Date().toISOString().split('T')[0]);
                setIsEditing(true);
            }
        } catch (error) {
            console.error('Error fetching legal document:', error);
            toast.error('Failed to load legal document');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDocument(activeType);
    }, [activeType]);

    // Handle Tab Switch
    const handleTabChange = (type) => {
        setActiveType(type);
        const path = type === 'PRIVACY_POLICY' ? 'privacy-policy' : 'terms-and-conditions';
        navigate(`/settings/legal-documents/${path}`);
    };

    // Save Draft
    const handleSaveDraft = async () => {
        if (!title.trim()) {
            toast.error('Please enter a document title');
            return;
        }
        if (!content.trim() || content === '<p><br></p>') {
            toast.error('Document content cannot be empty');
            return;
        }
        if (!version.trim()) {
            toast.error('Please enter a version string (e.g. 1.1)');
            return;
        }

        setLoading(true);
        try {
            const token = sessionStorage.getItem('Token') || localStorage.getItem('Token');
            const payload = {
                type: activeType,
                title,
                content,
                version,
                status: 'DRAFT',
                effectiveDate: effectiveDate || new Date().toISOString()
            };

            let response;
            // If we are editing an existing document ID and it is a DRAFT, update it; otherwise create a new draft version
            if (docData?._id && docData.status === 'DRAFT') {
                response = await putData(`legal-documents/${docData._id}`, payload, token);
            } else {
                response = await postData('legal-documents', payload, token);
            }

            if (response && (response.status === 200 || response.status === 201 || response.success !== false)) {
                toast.success('Draft saved successfully!');
                fetchDocument(activeType);
            } else {
                toast.error(response?.message || 'Failed to save draft');
            }
        } catch (error) {
            console.error('Save Draft Error:', error);
            toast.error(error.message || 'Failed to save draft');
        } finally {
            setLoading(false);
        }
    };

    // Publish Document
    const handlePublish = async () => {
        if (!content.trim() || content === '<p><br></p>') {
            toast.error('Document content cannot be empty');
            return;
        }

        setLoading(true);
        try {
            const token = sessionStorage.getItem('Token') || localStorage.getItem('Token');

            let targetId = docData?._id;

            // If current working state is unsaved or new version, save/create it first
            if (!targetId || docData?.status === 'PUBLISHED') {
                const createRes = await postData(
                    'legal-documents',
                    {
                        type: activeType,
                        title,
                        content,
                        version,
                        status: 'DRAFT',
                        effectiveDate: effectiveDate || new Date().toISOString()
                    },
                    token
                );
                if (createRes?.data?._id) {
                    targetId = createRes.data._id;
                } else if (createRes?.status === 200 || createRes?.status === 201) {
                    targetId = createRes.data?._id;
                }
            } else {
                // Ensure content and version updates are saved before publishing
                await putData(
                    `legal-documents/${targetId}`,
                    {
                        title,
                        content,
                        version,
                        effectiveDate: effectiveDate || new Date().toISOString()
                    },
                    token
                );
            }

            if (!targetId) {
                throw new Error('Unable to identify document ID for publishing');
            }

            const pubRes = await postData(`legal-documents/${targetId}/publish`, {}, token);
            if (pubRes && (pubRes.status === 200 || pubRes.success !== false)) {
                toast.success(`Published ${title} version ${version} successfully!`);
                fetchDocument(activeType);
            } else {
                toast.error(pubRes?.message || 'Failed to publish document');
            }
        } catch (error) {
            console.error('Publish Error:', error);
            toast.error(error.message || 'Failed to publish document');
        } finally {
            setLoading(false);
        }
    };

    // Fetch Version History
    const handleOpenHistory = async () => {
        setShowHistoryModal(true);
        setHistoryLoading(true);
        try {
            const token = sessionStorage.getItem('Token') || localStorage.getItem('Token');
            const targetId = docData?._id ? docData._id : activeType;
            const res = await getData(`legal-documents/${targetId}/history`, {}, token);
            if (res && (res.status === 200 || res.success !== false) && Array.isArray(res.data)) {
                setHistoryList(res.data);
            } else {
                setHistoryList([]);
            }
        } catch (error) {
            console.error('History Fetch Error:', error);
            toast.error('Failed to load version history');
        } finally {
            setHistoryLoading(false);
        }
    };

    // Create New Draft Version (Increment e.g., 1.0 -> 1.1)
    const handleCreateNewVersion = () => {
        const currentVerParts = version.split('.');
        let nextVersion = '1.1';
        if (currentVerParts.length === 2 && !isNaN(currentVerParts[1])) {
            nextVersion = `${currentVerParts[0]}.${parseInt(currentVerParts[1], 10) + 1}`;
        }
        setVersion(nextVersion);
        setStatus('DRAFT');
        setIsEditing(true);
        setDocData(prev => ({ ...prev, _id: null, status: 'DRAFT' }));
        toast.info(`Started new draft version ${nextVersion}`);
    };

    // Quill Toolbar Modules Configuration
    const quillModules = {
        toolbar: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: [] }],
            ['link', 'clean']
        ]
    };

    const formatUserDisplay = (userInfo) => {
        if (!userInfo) return 'SuperAdmin';
        if (typeof userInfo === 'string') return userInfo;
        const name = userInfo.name || userInfo.email;
        const role = userInfo.role ? ` (${userInfo.role})` : '';
        return name ? `${name}${role}` : 'SuperAdmin';
    };

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>

            {loading && <TVFMLoader />}

            {/* Main Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '24px', color: '#3C0008', fontWeight: 700 }}>
                        Legal Documents Management
                    </h2>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '14px' }}>
                        Manage Privacy Policy and Terms &amp; Conditions documents, manage draft versions, preview and publish live updates.
                    </p>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '10px', padding: '4px' }}>
                    <button
                        onClick={() => handleTabChange('PRIVACY_POLICY')}
                        style={{
                            padding: '8px 18px',
                            borderRadius: '8px',
                            border: 'none',
                            fontWeight: 600,
                            fontSize: '14px',
                            cursor: 'pointer',
                            background: activeType === 'PRIVACY_POLICY' ? '#3C0008' : 'transparent',
                            color: activeType === 'PRIVACY_POLICY' ? '#ffffff' : '#64748b',
                            transition: 'all 0.2s'
                        }}
                    >
                        <Icon icon="mdi:shield-account" style={{ marginRight: '6px', verticalAlign: '-2px' }} />
                        Privacy Policy
                    </button>

                    <button
                        onClick={() => handleTabChange('TERMS_AND_CONDITIONS')}
                        style={{
                            padding: '8px 18px',
                            borderRadius: '8px',
                            border: 'none',
                            fontWeight: 600,
                            fontSize: '14px',
                            cursor: 'pointer',
                            background: activeType === 'TERMS_AND_CONDITIONS' ? '#3C0008' : 'transparent',
                            color: activeType === 'TERMS_AND_CONDITIONS' ? '#ffffff' : '#64748b',
                            transition: 'all 0.2s'
                        }}
                    >
                        <Icon icon="mdi:file-certificate" style={{ marginRight: '6px', verticalAlign: '-2px' }} />
                        Terms &amp; Conditions
                    </button>
                </div>
            </div>

            {/* Document Metadata Card */}
            <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{
                            padding: '6px 14px',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: 700,
                            letterSpacing: '0.5px',
                            background: status === 'PUBLISHED' ? '#dcfce7' : '#fef3c7',
                            color: status === 'PUBLISHED' ? '#15803d' : '#b45309',
                            border: `1px solid ${status === 'PUBLISHED' ? '#86efac' : '#fde68a'}`
                        }}>
                            {status === 'PUBLISHED' ? '● Published' : '○ Draft'}
                        </span>
                        <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 600 }}>
                            Version: <strong style={{ color: '#0f172a' }}>v{version}</strong>
                        </span>
                    </div>

                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button
                            onClick={handleOpenHistory}
                            style={{
                                padding: '8px 14px',
                                borderRadius: '8px',
                                border: '1px solid #cbd5e1',
                                background: '#ffffff',
                                color: '#334155',
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <Icon icon="mdi:history" style={{ fontSize: '16px' }} />
                            Version History
                        </button>

                        <button
                            onClick={() => setShowPreviewModal(true)}
                            style={{
                                padding: '8px 14px',
                                borderRadius: '8px',
                                border: '1px solid #94a3b8',
                                background: '#f8fafc',
                                color: '#0f172a',
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <Icon icon="mdi:eye" style={{ fontSize: '16px' }} />
                            Preview
                        </button>

                        {status === 'PUBLISHED' && !isEditing ? (
                            <button
                                onClick={handleCreateNewVersion}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: '#3C0008',
                                    color: '#ffffff',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <Icon icon="mdi:pencil-plus" style={{ fontSize: '16px' }} />
                                Edit / Create New Version
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={handleSaveDraft}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: '8px',
                                        border: '1px solid #cbd5e1',
                                        background: '#ffffff',
                                        color: '#3C0008',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <Icon icon="mdi:content-save-outline" style={{ fontSize: '16px' }} />
                                    Save Draft
                                </button>

                                <button
                                    onClick={handlePublish}
                                    style={{
                                        padding: '8px 18px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        background: '#16a34a',
                                        color: '#ffffff',
                                        fontSize: '13px',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <Icon icon="mdi:publish" style={{ fontSize: '16px' }} />
                                    Publish
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Metadata Fields Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Document Title</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            disabled={!isEditing}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '14px',
                                outline: 'none',
                                background: isEditing ? '#ffffff' : '#f8fafc'
                            }}
                        />
                    </div>

                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Version Number</label>
                        <input
                            type="text"
                            value={version}
                            onChange={(e) => setVersion(e.target.value)}
                            disabled={!isEditing}
                            placeholder="e.g. 1.0, 1.1"
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '14px',
                                outline: 'none',
                                background: isEditing ? '#ffffff' : '#f8fafc'
                            }}
                        />
                    </div>

                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Effective Date</label>
                        <input
                            type="date"
                            value={effectiveDate}
                            onChange={(e) => setEffectiveDate(e.target.value)}
                            disabled={!isEditing}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '14px',
                                outline: 'none',
                                background: isEditing ? '#ffffff' : '#f8fafc'
                            }}
                        />
                    </div>

                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Last Updated Date</label>
                        <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#334155' }}>
                            {docData?.updatedAt ? new Date(docData.updatedAt).toLocaleString() : 'Not Saved Yet'}
                        </div>
                    </div>

                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Published Date</label>
                        <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#334155' }}>
                            {docData?.publishedAt ? new Date(docData.publishedAt).toLocaleString() : 'Not Published'}
                        </div>
                    </div>

                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Updated By</label>
                        <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#334155' }}>
                            {formatUserDisplay(docData?.updatedBy)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Rich Text Editor Section */}
            <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#0f172a', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon icon="mdi:format-text" style={{ fontSize: '20px', color: '#3C0008' }} />
                    Document HTML Content Editor
                </h3>

                <div style={{ minHeight: '400px' }}>
                    <ReactQuill
                        theme="snow"
                        value={content}
                        onChange={setContent}
                        readOnly={!isEditing}
                        modules={quillModules}
                        style={{ height: '360px', marginBottom: '50px' }}
                    />
                </div>
            </div>

            {/* PREVIEW MODAL */}
            {showPreviewModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(15, 23, 42, 0.75)', zIndex: 9999,
                    display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px'
                }}>
                    <div style={{
                        background: '#ffffff', borderRadius: '16px', width: '100%', maxWidth: '850px',
                        maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)'
                    }}>
                        <div style={{
                            padding: '16px 24px', background: '#3C0008', color: '#ffffff',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>
                                    Document Preview: {title} (v{version})
                                </h3>
                                <span style={{ fontSize: '12px', opacity: 0.85 }}>
                                    Status: {status} | Effective Date: {effectiveDate || 'N/A'}
                                </span>
                            </div>
                            <button
                                onClick={() => setShowPreviewModal(false)}
                                style={{ background: 'transparent', border: 'none', color: '#ffffff', fontSize: '24px', cursor: 'pointer' }}
                            >
                                &times;
                            </button>
                        </div>

                        <div style={{ padding: '30px', overflowY: 'auto', flex: 1, color: '#334155', lineHeight: 1.7, fontSize: '15px' }}>
                            <div
                                className="legal-doc-preview-content"
                                dangerouslySetInnerHTML={{ __html: content || '<p>No content entered yet.</p>' }}
                            />
                        </div>

                        <div style={{ padding: '16px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
                            <button
                                onClick={() => setShowPreviewModal(false)}
                                style={{
                                    padding: '8px 20px', borderRadius: '8px', border: 'none',
                                    background: '#3C0008', color: '#ffffff', fontWeight: 600, cursor: 'pointer'
                                }}
                            >
                                Close Preview
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* VERSION HISTORY MODAL */}
            {showHistoryModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(15, 23, 42, 0.75)', zIndex: 9999,
                    display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px'
                }}>
                    <div style={{
                        background: '#ffffff', borderRadius: '16px', width: '100%', maxWidth: '850px',
                        maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)'
                    }}>
                        <div style={{
                            padding: '16px 24px', background: '#3C0008', color: '#ffffff',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>
                                    Version History - {activeType === 'PRIVACY_POLICY' ? 'Privacy Policy' : 'Terms & Conditions'}
                                </h3>
                            </div>
                            <button
                                onClick={() => setShowHistoryModal(false)}
                                style={{ background: 'transparent', border: 'none', color: '#ffffff', fontSize: '24px', cursor: 'pointer' }}
                            >
                                &times;
                            </button>
                        </div>

                        <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
                            {historyLoading ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>Loading history...</div>
                            ) : historyList.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No version history recorded yet.</div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', textAlign: 'left' }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                            <th style={{ padding: '12px' }}>Version</th>
                                            <th style={{ padding: '12px' }}>Status</th>
                                            <th style={{ padding: '12px' }}>Updated By</th>
                                            <th style={{ padding: '12px' }}>Updated Date</th>
                                            <th style={{ padding: '12px' }}>Published Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyList.map((item) => (
                                            <tr key={item._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '12px', fontWeight: 700 }}>v{item.version}</td>
                                                <td style={{ padding: '12px' }}>
                                                    <span style={{
                                                        padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                                                        background: item.status === 'PUBLISHED' ? '#dcfce7' : '#fef3c7',
                                                        color: item.status === 'PUBLISHED' ? '#15803d' : '#b45309'
                                                    }}>
                                                        {item.status}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '12px' }}>{formatUserDisplay(item.updatedBy)}</td>
                                                <td style={{ padding: '12px' }}>{item.updatedAt ? new Date(item.updatedAt).toLocaleString() : 'N/A'}</td>
                                                <td style={{ padding: '12px' }}>{item.publishedAt ? new Date(item.publishedAt).toLocaleString() : '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div style={{ padding: '16px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
                            <button
                                onClick={() => setShowHistoryModal(false)}
                                style={{
                                    padding: '8px 20px', borderRadius: '8px', border: 'none',
                                    background: '#3C0008', color: '#ffffff', fontWeight: 600, cursor: 'pointer'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LegalDocumentManagement;
